import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Lock, Delete, UtensilsCrossed } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const LOGO_URL = '/assets/images/logos/xy43zlm2_logo-belgo-831e91aa.avif';
const PIG_CHEF_IMG = '/assets/images/site/fwsgrg5i_ChatGPT-Image-18-avr.-2026-09-h-41-min-13-s-32c78005.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComingSoonPage({ onAccessGranted }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const CODE_LENGTH = 4;

  const FALLBACK_CODE = '2245';

  const submitCode = useCallback(async (pin) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/settings/verify-code`, { code: pin });
      if (data.valid) {
        setSuccess(true);
        sessionStorage.setItem('belgo_access_granted', '1');
        setTimeout(() => onAccessGranted(), 600);
        return;
      }
    } catch {
      // API failed - fall through to fallback
    }
    // Fallback: vérifier le code localement si l'API échoue OU retourne false
    if (pin === FALLBACK_CODE) {
      setSuccess(true);
      sessionStorage.setItem('belgo_access_granted', '1');
      setTimeout(() => onAccessGranted(), 600);
    } else {
      setError('Code invalide');
      setCode('');
    }
    setLoading(false);
  }, [onAccessGranted]);

  const handlePinPress = useCallback((digit) => {
    if (loading || success) return;
    setError('');
    const newCode = code + digit;
    setCode(newCode);
    if (newCode.length === CODE_LENGTH) {
      submitCode(newCode);
    }
  }, [code, loading, success, submitCode]);

  const handleDelete = useCallback(() => {
    if (loading || success) return;
    setCode(prev => prev.slice(0, -1));
    setError('');
  }, [loading, success]);
  const [launchDate, setLaunchDate] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    axios.get(`${API}/settings/launch`).then(r => {
      if (r.data?.launch_date) setLaunchDate(new Date(r.data.launch_date));
    }).catch(() => {
      setLaunchDate(new Date('2026-05-20T11:00:00'));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!launchDate) return;
    const update = () => {
      const now = new Date();
      const diff = Math.max(0, launchDate.getTime() - now.getTime());
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [launchDate]);

  const pad = (v) => String(v).padStart(2, '0');

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-4 py-6 overflow-hidden" data-testid="coming-soon-page">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #f4d00d 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#e53a3a]" />

      <div className="relative z-10 w-full max-w-xl mx-auto text-center">
        {/* Logo */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
          <img src={LOGO_URL} alt="Resto Belgo" className="h-14 sm:h-18 mx-auto" />
        </motion.div>

        {/* Title */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
            Notre nouveau site
          </h1>
          <p className="text-[#f4d00d] font-heading text-2xl sm:text-4xl font-bold mb-3" style={{ textShadow: '0 0 20px rgba(244,208,13,0.3)' }}>
            arrive bientôt!
          </p>
        </motion.div>

        {/* Pig chef + countdown board composition */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.3, type: 'spring', damping: 15 }}
          className="relative inline-block"
        >
          {/* Countdown board that sits above the pig's raised hands */}
          <div
            className="relative mx-auto mb-[-40px] sm:mb-[-50px] z-10"
            style={{ maxWidth: '380px' }}
            data-testid="countdown"
          >
            {/* Wood frame */}
            <div className="bg-[#5c3a1e] rounded-xl p-[6px] shadow-2xl" style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.6)' }}>
              {/* Black chalkboard */}
              <div className="bg-[#1a1a1a] rounded-lg px-4 sm:px-6 py-4 sm:py-5 relative overflow-hidden">
                {/* Chalk border */}
                <div className="absolute inset-2 border border-dashed border-white/10 rounded-md pointer-events-none" />

                {/* Lancement dans */}
                <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-[4px] mb-3 font-body">
                  Lancement dans
                </p>

                {/* Countdown numbers */}
                <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                  {[
                    { val: timeLeft.days, lbl: 'Jours', first: true },
                    { val: timeLeft.hours, lbl: 'Heures', first: false },
                    { val: timeLeft.minutes, lbl: 'Min', first: false },
                    { val: timeLeft.seconds, lbl: 'Sec', first: false },
                  ].map((unit) => (
                    <div key={unit.lbl} className="flex items-center gap-1.5 sm:gap-2">
                      {!unit.first && <span className="text-[#e53a3a] font-bold text-lg sm:text-xl mt-[-14px]">:</span>}
                      <div className="flex flex-col items-center">
                        <div className="w-11 sm:w-14 h-11 sm:h-14 rounded-lg bg-[#f4d00d] text-black flex items-center justify-center text-lg sm:text-2xl font-bold font-heading shadow-md">
                          {pad(unit.val)}
                        </div>
                        <span className="text-[7px] sm:text-[9px] text-white/40 mt-1 uppercase tracking-wider">{unit.lbl}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pig chef image - arms raised to "hold" the board */}
          <div className="relative z-0">
            <img
              src={PIG_CHEF_IMG}
              alt="Cochon chef Belgo"
              className="w-[260px] sm:w-[320px] lg:w-[360px] h-auto mx-auto object-contain rounded-2xl"
              style={{ maskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)' }}
            />
          </div>
        </motion.div>

        {/* Subtitle */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-gray-400 text-sm mt-4 mb-1">
          Our new website is coming soon!
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="text-gray-500 text-xs mb-5">
          153 Champlain, Saint-Jean-sur-Richelieu | (450) 347-8635
        </motion.p>

        {/* PIN Pad */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="max-w-[260px] mx-auto" data-testid="access-code-form">
          {/* PIN dots */}
          <div className="flex items-center justify-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-gray-500 mr-1" />
            {Array.from({ length: CODE_LENGTH }).map((_, i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                  success ? 'bg-[#2E7D32] border-[#2E7D32] scale-110' :
                  i < code.length ? 'bg-[#f4d00d] border-[#f4d00d]' : 'bg-transparent border-white/30'
                }`}
              />
            ))}
          </div>
          {error && <p className="text-[#e53a3a] text-xs mt-1 mb-1 text-center" data-testid="access-code-error">{error}</p>}
          {!error && <div className="h-5" />}

          {/* Keypad grid */}
          <div className="grid grid-cols-3 gap-2" data-testid="pinpad">
            {['1','2','3','4','5','6','7','8','9','','0','del'].map((key) => {
              if (key === '') return <div key="empty" />;
              if (key === 'del') return (
                <button
                  key="del"
                  onClick={handleDelete}
                  className="h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-white/10 active:bg-white/20 transition-colors"
                  data-testid="pin-delete"
                >
                  <Delete className="w-5 h-5" />
                </button>
              );
              return (
                <button
                  key={key}
                  onClick={() => handlePinPress(key)}
                  disabled={loading || success}
                  className="h-12 rounded-xl bg-white/5 border border-white/10 text-white text-lg font-semibold hover:bg-white/10 active:bg-[#f4d00d] active:text-black transition-colors disabled:opacity-50"
                  data-testid={`pin-${key}`}
                >
                  {key}
                </button>
              );
            })}
          </div>

          {/* Public menu access */}
          <div className="mt-6 text-center">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-black bg-[#f4d00d] hover:bg-[#d4b800] transition-colors"
              data-testid="view-menu-from-coming-soon"
            >
              <UtensilsCrossed className="w-4 h-4" /> Voir le menu / View menu
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
